export interface StorageOptions {
  ttl?: number; // Time-to-live in seconds
}

interface StoragePayload<T> {
  value: T;
  expiry: number | null;
}

export const LocalStorageUtils = {
  /**
   * Helper to check if we are in the browser (SSR safety)
   */
  isBrowser(): boolean {
    // In Angular, you'd usually inject PLATFORM_ID,
    // but for a pure util, checking window is more direct.
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  },

  set<T>(key: string, value: T, options?: StorageOptions): void {
    if (!this.isBrowser()) return;

    const payload: StoragePayload<T> = {
      value,
      expiry: options?.ttl ? Date.now() + options.ttl * 1000 : null,
    };

    localStorage.setItem(key, JSON.stringify(payload));
  },

  get<T>(key: string): T | null {
    if (!this.isBrowser()) return null;

    const data = localStorage.getItem(key);
    if (!data) return null;

    try {
      const payload: StoragePayload<T> = JSON.parse(data);

      if (payload.expiry && Date.now() > payload.expiry) {
        localStorage.removeItem(key);
        return null;
      }

      return payload.value;
    } catch {
      return null; // Handle malformed JSON
    }
  },

  remove(key: string): void {
    if (this.isBrowser()) {
      localStorage.removeItem(key);
    }
  },

  clear(): void {
    if (this.isBrowser()) {
      localStorage.clear();
    }
  }
};
